import type { ScriptWord, ParsedScript } from "../types";
import { normalizeWord, textTokens } from "./script";

export interface MatchOptions {
  final?: boolean;
  session?: string;
  confidenceThreshold?: number;
}
export interface SpeechMatch {
  wordIndex: number;
  confidence: number;
  matched: boolean;
  reason:
    | "matched"
    | "duplicate"
    | "silence"
    | "uncertain"
    | "ambiguous"
    | "stabilized";
}
type Token = { value: string; index: number };
const units: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
  un: 1,
  une: 1,
  deux: 2,
  trois: 3,
  quatre: 4,
  cinq: 5,
  sept: 7,
  huit: 8,
  neuf: 9,
  dix: 10,
  onze: 11,
  douze: 12,
  treize: 13,
  quatorze: 14,
  quinze: 15,
  seize: 16,
  vingt: 20,
  trente: 30,
  quarante: 40,
  cinquante: 50,
  soixante: 60,
  null: 0,
  eins: 1,
  ein: 1,
  eine: 1,
  zwei: 2,
  drei: 3,
  vier: 4,
  funf: 5,
  sechs: 6,
  sieben: 7,
  acht: 8,
  neun: 9,
  zehn: 10,
  elf: 11,
  zwolf: 12,
  dreizehn: 13,
  vierzehn: 14,
  funfzehn: 15,
  sechzehn: 16,
  siebzehn: 17,
  achtzehn: 18,
  neunzehn: 19,
  zwanzig: 20,
  dreissig: 30,
  vierzig: 40,
  funfzig: 50,
  sechzig: 60,
  siebzig: 70,
  achtzig: 80,
  neunzig: 90,
};
const multipliers: Record<string, number> = {
  hundred: 100,
  hundert: 100,
  cent: 100,
  cents: 100,
  thousand: 1000,
  tausend: 1000,
  mille: 1000,
  million: 1_000_000,
  millions: 1_000_000,
};
function canonical(value: string) {
  const n = normalizeWord(value);
  if (n in units) return String(units[n]);
  const compound = n.match(/^(.+)und(.+)$/);
  if (compound && units[compound[1]] !== undefined && units[compound[2]] >= 20)
    return String(units[compound[1]] + units[compound[2]]);
  return n;
}
function tokens(
  words: { text: string; index: number; cue?: boolean }[],
): Token[] {
  const result: Token[] = [];
  for (let i = 0; i < words.length; i++) {
    if (words[i].cue) continue;
    const numeric = words[i].text
      .replace(/[.,;:!?]+$/g, "")
      .replace(/^[€$£¥]/, "")
      .replace(/%$/, "");
    if (/^\d[\d.,]*$/.test(numeric)) {
      const grouped = /^\d{1,3}(,\d{3})+(\.\d+)?$/.test(numeric)
        ? numeric.replace(/,/g, "")
        : numeric;
      const decimal = grouped.match(/^(\d+)[.,](\d+)$/);
      if (decimal) {
        result.push(
          { value: String(Number(decimal[1])), index: words[i].index },
          { value: "point", index: words[i].index },
          { value: decimal[2], index: words[i].index },
        );
      } else
        result.push({
          value: grouped.replace(/[.,]/g, ""),
          index: words[i].index,
        });
      continue;
    }
    const word = normalizeWord(words[i].text);
    if (!word) continue;
    if (["point", "virgule", "komma"].includes(word)) {
      result.push({ value: "point", index: words[i].index });
      continue;
    }
    if (word in units || word in multipliers) {
      let group = 0,
        total = 0,
        j = i,
        last = i,
        count = 0;
      for (; j < words.length && j < i + 10; j++) {
        if (words[j].cue) break;
        const n = normalizeWord(words[j].text);
        if (
          (n === "and" || n === "et") &&
          count &&
          j + 1 < words.length &&
          normalizeWord(words[j + 1].text) in units
        ) {
          last = j;
          continue;
        }
        if (n in units) {
          const v = units[n];
          if (count && v >= 20 && group > 0 && group < 100) break;
          if (count && v < 10 && group > 0 && group < 20) break;
          group += v;
          count++;
          last = j;
        } else if (n in multipliers) {
          const multiplier = multipliers[n];
          if (multiplier === 100) group = Math.max(group, 1) * 100;
          else {
            total += Math.max(group, 1) * multiplier;
            group = 0;
          }
          count++;
          last = j;
        } else break;
      }
      result.push({ value: String(total + group), index: words[last].index });
      i = last;
    } else {
      // ASR often removes apostrophes or splits hyphenated names. Normalization keeps letters stable.
      result.push({ value: canonical(words[i].text), index: words[i].index });
    }
  }
  return result;
}

/** Pure bounded phrase alignment. Interim/final repeats are idempotent; large seeks need a long unique phrase. */
export class SpeechMatcher {
  private script: Token[];
  private occurrences = new Map<string, number[]>();
  private position = 0;
  private lastTranscript = "";
  private lastSession = "";
  private utteranceStart = 0;
  private pendingRepeat = false;
  // Two fixed rows cover the maximum 18-token query plus its six-token context.
  private alignmentRows = [new Float64Array(25), new Float64Array(25)];
  constructor(
    input: ParsedScript | ScriptWord[] | string,
    private options: {
      lookAhead?: number;
      lookBehind?: number;
      confidenceThreshold?: number;
    } = {},
  ) {
    const words =
      typeof input === "string"
        ? textTokens(input).map((token, index) => ({ text: token.text, index }))
        : Array.isArray(input)
          ? input
          : input.words;
    this.script = tokens(words);
    this.script.forEach((word, i) => {
      const list = this.occurrences.get(word.value) || [];
      list.push(i);
      this.occurrences.set(word.value, list);
    });
  }
  setPosition(wordIndex: number) {
    this.position = Math.max(0, Math.floor(wordIndex));
    this.lastTranscript = "";
    this.utteranceStart = this.position;
    this.pendingRepeat = false;
  }
  reset(wordIndex = 0) {
    this.setPosition(wordIndex);
    this.lastSession = "";
  }
  getPosition() {
    return this.position;
  }
  match(transcript: string, options: MatchOptions = {}): SpeechMatch {
    const query = tokens(
      textTokens(transcript.slice(-4000)).map((token, index) => ({
        text: token.text,
        index,
      })),
    ).slice(-18);
    const normalized = query.map((x) => x.value).join(" ");
    const session = options.session || "";
    const sameUtterance = !!session && session === this.lastSession;
    const result = (
      reason: SpeechMatch["reason"],
      confidence = 0,
    ): SpeechMatch => ({
      wordIndex: this.position,
      confidence,
      matched: reason === "matched",
      reason,
    });
    if (!query.length) return result("silence");
    if (session && !sameUtterance) {
      this.utteranceStart = this.position;
      this.pendingRepeat = false;
    }
    if (
      normalized === this.lastTranscript &&
      session === this.lastSession &&
      !(options.final !== false && this.pendingRepeat)
    )
      return result("duplicate");
    this.lastTranscript = normalized;
    this.lastSession = session;
    const behind = this.options.lookBehind ?? 20,
      ahead = this.options.lookAhead ?? 140;
    const candidates = new Set<number>();
    // Heard suffix words are possible endpoints themselves. Projecting past
    // an earlier suffix word would turn trailing filler into unread script.
    for (let qi = Math.max(0, query.length - 3); qi < query.length; qi++) {
      const locations = this.occurrences.get(query[qi].value) || [];
      // Occurrences are sorted. Skip directly to the local window instead of scanning
      // every appearance of common words in an otherwise arbitrarily long script.
      let low = 0,
        high = locations.length;
      while (low < high) {
        const middle = (low + high) >>> 1;
        if (this.script[locations[middle]].index < this.position - behind)
          low = middle + 1;
        else high = middle;
      }
      for (let i = low; i < locations.length; i++) {
        const si = locations[i];
        if (this.script[si].index > this.position + ahead) break;
        candidates.add(si);
      }
    }
    // Whole-script reacquisition anchors on the rarest recognized word, bounding repeated boilerplate work.
    // Highly repetitive phrases cannot uniquely identify a distant place; operator seeking handles that case.
    if (query.length >= 7) {
      let rare: { qi: number; locations: number[] } | undefined;
      query.forEach((word, qi) => {
        const locations = this.occurrences.get(word.value);
        if (
          locations?.length &&
          (!rare || locations.length < rare.locations.length)
        )
          rare = { qi, locations };
      });
      if (rare && rare.locations.length <= 256)
        for (const location of rare.locations)
          for (let adjustment = -1; adjustment <= 1; adjustment++)
            candidates.add(location + query.length - 1 - rare.qi + adjustment);
    }
    let best: { end: number; confidence: number; score: number } | null = null,
      second: { end: number; confidence: number; score: number } | null = null;
    // A fuzzy phrase score must never invent a reading endpoint. Anchoring a
    // rare early word and projecting the remaining ASR length can overshoot
    // into an unread sentence when an interim result adds a hallucinated tail.
    // Require an actually aligned ending token, allowing up to two trailing
    // fillers. Merely finding the endpoint somewhere in that suffix would let
    // a substituted tail advance into a later common word such as "the".
    const endingTokens = new Set(query.slice(-3).map((token) => token.value));
    for (const end of candidates) {
      if (end < 0 || end >= this.script.length) continue;
      if (!endingTokens.has(this.script[end].value)) continue;
      const start = Math.max(0, end - query.length - 5);
      const length = end - start + 1;
      // Edit distance allows skipped words and ad-lib fillers. Free prefix lets recognition start mid-sentence.
      // Reuse bounded scratch storage: no script slice or per-row allocation in the hot path.
      let [prev, row] = this.alignmentRows;
      prev.fill(0, 0, length + 1);
      let anchoredCost = Infinity;
      for (let q = 1; q <= query.length; q++) {
        if (q >= query.length - 2 && query[q - 1].value === this.script[end].value) {
          // Force this exact token-to-endpoint diagonal. Unsupported trailing
          // words cost slightly more than ordinary interior ad-libs, so a
          // later heard token is preferred over an earlier common-word anchor.
          const trailing = query.length - q;
          anchoredCost = Math.min(anchoredCost, prev[length - 1] + trailing * 1.5);
        }
        row[0] = q;
        for (let s = 1; s <= length; s++)
          row[s] = Math.min(
            prev[s] + 1,
            row[s - 1] + 0.85,
            prev[s - 1] +
              (query[q - 1].value === this.script[start + s - 1].value ? 0 : 1),
          );
        const previous = prev;
        prev = row;
        row = previous;
      }
      const confidence = Math.max(0, 1 - anchoredCost / query.length);
      const distance = this.script[end].index - this.position;
      const far = distance > ahead || distance < -behind;
      if (far && (query.length < 7 || confidence < 0.94)) continue;
      if (
        query.length < 3 &&
        (distance < -3 || distance > (query.length === 1 ? 3 : 10))
      )
        continue;
      const score =
        confidence - Math.abs(distance) * 0.0003 - (distance < -3 ? 0.025 : 0);
      const candidate = { end, confidence, score };
      if (!best || score > best.score) {
        second = best;
        best = candidate;
      } else if (!second || score > second.score) second = candidate;
    }
    if (
      !best ||
      best.confidence <
        (options.confidenceThreshold ??
          this.options.confidenceThreshold ??
          0.68)
    )
      return result("uncertain", best?.confidence || 0);
    const far = Math.abs(this.script[best.end].index - this.position) > ahead;
    if (far && second && Math.abs(best.confidence - second.confidence) < 0.05)
      return result("ambiguous", best.confidence);
    // Recognition rewrites interim text frequently. Hold the furthest read word through same-utterance corrections.
    // A new finalized utterance can intentionally repeat an earlier passage and move backwards.
    if (
      this.script[best.end].index < this.position &&
      (options.final === false ||
        (sameUtterance && this.position > this.utteranceStart))
    ) {
      this.pendingRepeat =
        options.final === false && this.position <= this.utteranceStart;
      return result("stabilized", best.confidence);
    }
    this.pendingRepeat = false;
    this.position = this.script[best.end].index;
    return result("matched", best.confidence);
  }
}
